import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiLockLine, RiCheckLine, RiArrowRightLine, RiBookOpenLine } from 'react-icons/ri';
import { C } from '../../data/constants';
import { getGrammarChapters } from '../../api/grammar.api';
import { LoadingScreen, EmptyState } from '../../components/shared/UI';
import { useAppLevel } from '../../components/app/AppTopbar';

/* ══════════════════════════════════════════════════════════════
   CHAPTER SHELF
   25 chapters laid out like the contents page of a textbook, which
   is the metaphor the course already follows. Chapters unlock in
   order, so at a glance you see how far you have come and exactly
   which one is next — the same job the learning path does for kana.
══════════════════════════════════════════════════════════════ */

export default function GrammarChapters() {
  const navigate = useNavigate();
  const [chapters, setChapters] = useState(null);
  const [error, setError] = useState('');
  const { level } = useAppLevel();

  const load = useCallback(() => {
    setError('');
    setChapters(null);
    getGrammarChapters(level)
      .then(res => setChapters(res.data.chapters))
      .catch(err => setError(err.message));
  }, [level]);

  useEffect(() => { load(); }, [load]);

  if (error && !chapters) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: C.red, fontSize: 13, marginBottom: 16 }}>{error}</div>
        <button className="btn-ghost" onClick={load}>Try again</button>
      </div>
    );
  }

  if (!chapters) return <LoadingScreen label="Loading your grammar course" />;

  const done = chapters.filter(c => c.complete).length;
  const started = chapters.find(c => !c.locked && !c.complete);

  return (
    <div className="page-container gr-shelf-page anim-fade-up">
      <header className="gr-shelf-head">
        <div>
          <span className="gr-shelf-kicker">JLPT {level} · {chapters.length} chapter{chapters.length === 1 ? '' : 's'}</span>
          <h1>Grammar</h1>
          <p>Work through the chapters in order — each one builds on the last.</p>
        </div>
        <div className="gr-shelf-progress">
          <strong>{done}</strong>
          <span>of {chapters.length} done</span>
        </div>
      </header>

      {started && (
        <button className="gr-continue" onClick={() => navigate(`/grammar/${started.number}`)}>
          <span className="gr-continue-copy">
            <em>Continue</em>
            <strong>Chapter {started.number} · {started.titleEn}</strong>
            <small>{started.partsDone} of {started.partsTotal} parts</small>
          </span>
          <RiArrowRightLine size={20} />
        </button>
      )}

      {chapters.length === 0 ? (
        <div className="ks-card">
          <EmptyState
            icon={RiBookOpenLine}
            title={`No ${level} chapters yet`}
            sub={`Publish a ${level} chapter from the admin panel and it will appear here.`}
          />
        </div>
      ) : (
        <div className="gr-shelf">
          {chapters.map(ch => {
            const empty = ch.partsTotal === 0;
            const state = ch.complete ? 'done' : ch.locked ? 'locked' : 'open';
            return (
              <button
                key={ch._id}
                type="button"
                className={`gr-chapter ${state}${empty ? ' empty' : ''}`}
                data-num={ch.number}
                disabled={ch.locked || empty}
                onClick={() => navigate(`/grammar/${ch.number}`)}
                title={
                  empty ? 'Not written yet'
                    : ch.locked ? `Finish chapter ${ch.number - 1} first`
                      : ch.title
                }
              >
                <span className="gr-chapter-num">
                  {ch.complete ? <RiCheckLine size={18} /> : ch.locked ? <RiLockLine size={15} /> : ch.number}
                </span>

                {/* English leads. A learner scanning this grid is deciding
                    what to study, and じこしょうかい cannot be scanned by
                    someone who has not learned it yet — which is precisely
                    who the chapter list is for. The Japanese stays as the
                    subtitle, so the title is still there to grow into. */}
                <span className="gr-chapter-copy">
                  <strong>{ch.titleEn}</strong>
                  <span className="gr-chapter-jp">{ch.title}</span>
                  {ch.summary && <span className="gr-chapter-sub">{ch.summary}</span>}
                </span>

                <span className="gr-chapter-foot">
                  {empty ? (
                    <em>In preparation</em>
                  ) : (
                    <>
                      <span className="gr-chapter-bar">
                        <i style={{ width: `${ch.partsTotal ? (ch.partsDone / ch.partsTotal) * 100 : 0}%` }} />
                      </span>
                      <em>{ch.partsDone}/{ch.partsTotal}</em>
                    </>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
